import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage } from 'react-intl';
import { ReactNode, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Paper from '@mui/material/Paper';
import { PageHeader } from './PageHeader';
import { fromAccounts, fromLayout, useAppDispatch } from '../../store';
import { LoadingProgress } from '../../layout';

const useStyles = makeStyles(theme => ({
  pageContainer: {
    overflow: 'scroll',
    height: '100%',
  },
  pageHeader: {
    height: theme.spacing(12),
    padding: theme.spacing(1, 5, 0, 5),
    [theme.breakpoints.down('tablet')]: {
      padding: theme.spacing(1, 5),
      height: theme.spacing(8.25),
    },
  },
  pageBody: {
    padding: theme.spacing(0, 5),
  },
  pageTopBanner: {
    padding: theme.spacing(3.5, 5, 3.5, 5),
    borderBottom: '1px solid #e5e7eb',
    // Hide the wrapper (and its divider) when the banner renders nothing —
    // avoids an empty bordered strip when the inner component returns null.
    '&:empty': {
      display: 'none',
    },
    [theme.breakpoints.down('tablet')]: {
      padding: theme.spacing(3, 5, 3, 5),
    },
  },
  pageTitle: {
    fontWeight: 500,
    fontSize: 22,
    [theme.breakpoints.down('tablet')]: {
      fontSize: 18,
    },
    [theme.breakpoints.down('mobile')]: {
      fontSize: 16,
    },
  },
}));

interface PageContainerProps {
  id: string;
  headerId: string;
  headerValue?: any;
  titleId?: string;
  titleValues?: any;
  topBanner?: ReactNode;
  background?: string;
  hideHeader?: boolean;
  fetchData: () => Promise<void>;
  children: ReactNode;
}

export const Page = (
  {
    id,
    headerId,
    headerValue,
    titleId,
    titleValues,
    topBanner,
    background,
    hideHeader,
    fetchData,
    children,
  }: PageContainerProps,
) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const account = useSelector(fromAccounts.selectSelectedAccount);
  const refetchData = useSelector(fromLayout.selectRefetchData);
  const [fetchingData, setFetchingData] = useState(false);

  useEffect(() => {
    dispatch(fromLayout.setLoading(false));
  }, []);

  useEffect(() => {
    if (refetchData) {
      setFetchingData(refetchData);
      fetchData()
        .catch(() => {
        })
        .finally(() => {
          setFetchingData(false);
          dispatch(fromLayout.setRefetchData(false));
        });
    }
  }, [refetchData]);

  useEffect(() => {
    const initiate = async () => {
      setFetchingData(true);
      await fetchData();
    };
    initiate()
      .finally(() => setFetchingData(false))
      .catch(() => {
      });
  }, [account]);

  return (
    <Paper
      id={`page-${id}-container`}
      className={classes.pageContainer}
      elevation={0}
      square
      sx={background ? { backgroundColor: background } : undefined}
    >
      {topBanner && (
        <Box
          id={`page-${id}-top-banner`}
          className={classes.pageTopBanner}
        >
          {topBanner}
        </Box>
      )}
      {!hideHeader && (
      <Box
        id={`page-${id}-header`}
        className={classes.pageHeader}
      >
        <PageHeader
          headerId={headerId}
          headerValue={headerValue}
        />
        {titleId
          ? (
            <Box id={`page-${id}-title`}>
              <Typography
                variant="h6"
                className={classes.pageTitle}
              >
                <FormattedMessage id={titleId} values={titleValues} />
              </Typography>
            </Box>
          )
          : (
            <Box id={`page-${id}-title`}>
              <Typography
                variant="h6"
                className={classes.pageTitle}
              >
                <FormattedMessage
                  id="page.account.info"
                  values={{
                    accountName: account?.name,
                    accountId: account?.id,
                  }}
                />
              </Typography>
            </Box>
          )}
      </Box>
      )}
      <Box
        id={`page-${id}-content`}
        className={classes.pageBody}
        overflow="auto"
      >
        {fetchingData
          ? (
            <LoadingProgress fullscreen />
          )
          : children}
      </Box>
    </Paper>
  );
};
